import React, { useState } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  UsersIcon,
  ArrowRightOnRectangleIcon,
  MapIcon,
  BriefcaseIcon,
  TruckIcon,
  QuestionMarkCircleIcon,
  DocumentTextIcon,
  BuildingOffice2Icon,
  ShieldCheckIcon,
  AdjustmentsHorizontalIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  BellIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '@/shared/contexts'
import { Button } from '../Button'

type Icon = React.ComponentType<{ className?: string | undefined }>

interface NavLink {
  kind: 'link'
  name: string
  href: string
  icon?: Icon
  permission?: { section: string; action: string }
  superadminOnly?: boolean
  visibleWhen?: (ctx: {
    hasPermission: (section: string, action: string) => boolean
    isSuperadmin: boolean
  }) => boolean
}

interface NavGroup {
  kind: 'group'
  name: string
  children: NavLink[]
}

interface NavSection {
  kind: 'section'
  name: string
  icon: Icon
  description?: string
  children: Array<NavLink | NavGroup>
}

const navigation: NavSection[] = [
  {
    kind: 'section',
    name: 'Заказы',
    icon: ClipboardDocumentListIcon,
    description: 'Оперативный контроль доставок',
    children: [
      { kind: 'link', name: 'Заказы', href: '/orders',
        icon: ClipboardDocumentListIcon,
        permission: { section: 'orders', action: 'view' } },
    ],
  },
  {
    kind: 'section',
    name: 'Курьеры',
    icon: UsersIcon,
    description: 'Работа с курьерами и их финансовыми операциями',
    children: [
      { kind: 'link', name: 'Курьеры', href: '/couriers', icon: UsersIcon,
        permission: { section: 'couriers', action: 'view' } },
      // Fallback для ролей только с правом модерации: иначе у них нет пункта меню
      // к очереди заявок (вкладки «На модерации» доступны лишь внутри страниц раздела).
      { kind: 'link', name: 'На модерации', href: '/moderation', icon: ClockIcon,
        visibleWhen: ({ hasPermission, isSuperadmin }) =>
          !isSuperadmin
          && hasPermission('moderation', 'view')
          && !hasPermission('couriers', 'view') },
      { kind: 'link', name: 'Тарифы курьеров', href: '/tariffs', icon: AdjustmentsHorizontalIcon,
        permission: { section: 'tariffs', action: 'view' } },
      { kind: 'link', name: 'Начисления и списания', href: '/courier-ledger', icon: ClipboardDocumentListIcon,
        permission: { section: 'tariffs', action: 'view_card' } },
    ],
  },
  {
    kind: 'section',
    name: 'Юридические лица',
    icon: BuildingOffice2Icon,
    description: 'Клиенты платформы, подписки и расчёты',
    children: [
      { kind: 'link', name: 'Клиенты', href: '/legal-entities', icon: BuildingOffice2Icon,
        permission: { section: 'legal_entities', action: 'view' } },
      { kind: 'link', name: 'Тарифы для юрлиц', href: '/legal-entity-tariffs', icon: AdjustmentsHorizontalIcon,
        permission: { section: 'clientTariffs', action: 'view' } },
      { kind: 'link', name: 'Счета и операции', href: '/legal-entity-ledger', icon: ClipboardDocumentListIcon,
        permission: { section: 'balance', action: 'view' } },
    ],
  },
  {
    kind: 'section',
    name: 'Настройки приложения',
    icon: AdjustmentsHorizontalIcon,
    description: 'Справочники и контент, доступные курьерам в приложении',
    children: [
      { kind: 'group', name: 'Анкета курьера', children: [
        { kind: 'link', name: 'Регионы', href: '/dictionaries/regions', icon: MapIcon,
          permission: { section: 'dictionaries', action: 'view' } },
        { kind: 'link', name: 'Виды деятельности', href: '/activity-types', icon: BriefcaseIcon,
          permission: { section: 'dictionaries', action: 'view' } },
        { kind: 'link', name: 'Виды транспорта', href: '/transport-types', icon: TruckIcon,
          permission: { section: 'dictionaries', action: 'view' } },
      ] },
      { kind: 'group', name: 'Контент приложения', children: [
        { kind: 'link', name: 'FAQ', href: '/faq', icon: QuestionMarkCircleIcon,
          permission: { section: 'faq', action: 'view' } },
        { kind: 'link', name: 'Версии приложения', href: '/app-versions', icon: DocumentTextIcon,
          permission: { section: 'appVersions', action: 'view' } },
      ] },
      { kind: 'group', name: 'Уведомления', children: [
        { kind: 'link', name: 'Пуш-уведомления', href: '/push', icon: BellIcon,
          permission: { section: 'push', action: 'view' } },
      ] },
    ],
  },
  {
    kind: 'section',
    name: 'Роли и доступы',
    icon: ShieldCheckIcon,
    description: 'Управление сотрудниками и правами доступа',
    children: [
      { kind: 'link', name: 'Роли', href: '/roles', icon: ShieldCheckIcon, superadminOnly: true },
      { kind: 'link', name: 'Сотрудники', href: '/employees', icon: UsersIcon, superadminOnly: true },
    ],
  },
]

const standaloneNavigation: NavLink[] = [
  { kind: 'link', name: 'Логи API', href: '/logs', icon: DocumentTextIcon,
    permission: { section: 'audit', action: 'view' } },
]

interface AdminLayoutProps {
  children: React.ReactNode
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  const { user, logout, hasPermission, isSuperadmin } = useAuth()
  const location = useLocation()

  const isCurrentPath = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href)

  const canSee = (link: NavLink): boolean => {
    if (link.visibleWhen) {
      return link.visibleWhen({ hasPermission, isSuperadmin })
    }
    if (link.superadminOnly) return isSuperadmin
    if (link.permission) return hasPermission(link.permission.section, link.permission.action)
    return true
  }

  const sectionLinks = (section: NavSection): NavLink[] =>
    section.children.flatMap((child) => (child.kind === 'group' ? child.children : [child]))

  const sectionHasActive = (section: NavSection) =>
    sectionLinks(section).some((link) => isCurrentPath(link.href))

  const visibleSections = navigation
    .map((section) => ({
      section,
      children: section.children
        .map((child) =>
          child.kind === 'group'
            ? { kind: 'group' as const, name: child.name, links: child.children.filter(canSee) }
            : canSee(child) ? child : null,
        )
        .filter((child): child is NonNullable<typeof child> =>
          child !== null && (child.kind === 'link' || child.links.length > 0),
        ),
    }))
    .filter((entry) => entry.children.length > 0)

  const visibleStandalone = standaloneNavigation.filter(canSee)

  const renderLink = (link: NavLink, indented = true) => {
    const isActive = isCurrentPath(link.href)
    const LinkIcon = link.icon

    if (!sidebarOpen) {
      return (
        <Link
          key={link.name}
          to={link.href as never}
          title={link.name}
          className={`group flex items-center justify-center px-2 py-2 rounded-md transition-colors ${
            isActive
              ? 'bg-gray-100 text-gray-900'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          {LinkIcon && (
            <LinkIcon
              className={`h-5 w-5 ${
                isActive ? 'text-gray-600' : 'text-gray-400 group-hover:text-gray-600'
              }`}
            />
          )}
        </Link>
      )
    }

    return (
      <Link
        key={link.name}
        to={link.href as never}
        className={`group flex items-center ${indented ? 'pl-8' : 'pl-2'} pr-2 py-2 text-sm rounded-md transition-colors ${
          isActive
            ? 'bg-gray-100 text-gray-900 font-medium border-r-2 border-gray-600'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }`}
      >
        {LinkIcon && (
          <LinkIcon
            className={`flex-shrink-0 h-4 w-4 mr-2 ${
              isActive ? 'text-gray-600' : 'text-gray-400 group-hover:text-gray-600'
            }`}
          />
        )}
        <span>{link.name}</span>
      </Link>
    )
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar */}
      <div className={`bg-white border-r border-gray-200 transition-all duration-300 flex flex-col h-full overflow-y-auto ${
        sidebarOpen ? 'w-64' : 'w-16'
      }`}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between px-6 py-6">
          {sidebarOpen && (
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gray-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">Z</span>
              </div>
              <div className="ml-3">
                <h1 className="text-lg font-semibold text-gray-900">Zveno</h1>
                <p className="text-xs text-gray-500">Admin Panel</p>
              </div>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            {sidebarOpen ? (
              <ChevronLeftIcon className="h-5 w-5" />
            ) : (
              <ChevronRightIcon className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1">
          {visibleSections.map(({ section, children }) => {
            const SectionIcon = section.icon
            const active = sectionHasActive(section)

            if (!sidebarOpen) {
              const href = sectionLinks(section).find(canSee)?.href
              return (
                <Link
                  key={section.name}
                  to={(href || '/') as never}
                  title={section.name}
                  className={`group flex items-center justify-center px-2 py-2 rounded-md transition-colors ${
                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <SectionIcon className={`h-5 w-5 ${active ? 'text-gray-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                </Link>
              )
            }

            const expanded = openSections[section.name] ?? active
            return (
              <div key={section.name}>
                <button
                  type="button"
                  onClick={() =>
                    setOpenSections((prev) => ({ ...prev, [section.name]: !expanded }))
                  }
                  className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                    active ? 'text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <SectionIcon className={`flex-shrink-0 h-5 w-5 ${active ? 'text-gray-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                  <span className="ml-3 flex-1 text-left">{section.name}</span>
                  <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                </button>
                {expanded && (
                  <div className="mt-0.5 mb-1 space-y-0.5">
                    {children.map((child) =>
                      child.kind === 'group' ? (
                        <div key={`group-${child.name}`}>
                          <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                            {child.name}
                          </div>
                          {child.links.map((link) => renderLink(link))}
                        </div>
                      ) : (
                        renderLink(child)
                      ),
                    )}
                  </div>
                )}
              </div>
            )
          })}

          {visibleStandalone.length > 0 && (
            <div className="pt-2 mt-2 border-t border-gray-200 space-y-0.5">
              {visibleStandalone.map((link) => renderLink(link, false))}
            </div>
          )}
        </nav>

        {/* User Info & Logout */}
        <div className="border-t border-gray-200 p-4">
          {sidebarOpen ? (
            <div className="space-y-3">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <span className="text-gray-600 font-medium text-sm">
                    {user?.name?.[0] || user?.login?.[0] || 'A'}
                  </span>
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.name || user?.login || 'Администратор'}
                  </p>
                  {user?.email && (
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="w-full justify-start text-gray-600 hover:text-gray-900"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                Выйти
              </Button>
            </div>
          ) : (
            <div className="flex flex-col space-y-2">
              <div
                className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center mx-auto"
                title={user?.name || user?.login || 'Администратор'}
              >
                <span className="text-gray-600 font-medium text-sm">
                  {user?.name?.[0] || user?.login?.[0] || 'A'}
                </span>
              </div>
              <button
                onClick={logout}
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors mx-auto"
                title="Выйти"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full overflow-y-auto">
        {children}
      </div>
    </div>
  )
}
